export const SUPPORTED_LNGS = ['en', 'nl', 'de', 'es', 'it', 'fr', 'pt'];

/**
 * Base language of a tag (`de-AT` -> `de`, `DE` -> `de`) when it is supported,
 * else undefined. Anything after the first `-`/`_` is dropped, so a crafted
 * `?lng=de-x<script>` can never reach the cookie, localStorage or `Intl`.
 */
export const toSupportedLng = (raw?: string | null): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const base = raw.split(/[-_]/)[0].trim().toLowerCase();
  return SUPPORTED_LNGS.includes(base) ? base : undefined;
};

// Detected values (query, cookie, localStorage, <html lang>) are reduced to
// the base language. An unsupported value is passed on unchanged and skipped
// by i18next, so detection continues with the next source as before.
export const convertDetectedLanguage = (raw: string): string =>
  toSupportedLng(raw) ?? raw;

// `?lng=de` comes first: the website appends it to the editor handoff URL, so
// the visitor keeps the language they were reading. A value outside
// SUPPORTED_LNGS is skipped and detection continues as before. `navigator` is
// left out on purpose: htmlTag (`<html lang="en">`) always answers before it,
// so it would never be reached, and moving it earlier would change the
// language of existing users.
export const DETECTION_OPTIONS = {
  order: ['querystring', 'cookie', 'localStorage', 'htmlTag', 'path', 'subdomain'],
  lookupQuerystring: 'lng',
  caches: ['cookie', 'localStorage'],
  cookieMinutes: 60 * 24 * 365, // 1 year
  convertDetectedLanguage,
};

// `de-AT` / `nl-BE` are reduced to `de` / `nl` by convertDetectedLanguage, so
// i18n.language is always one of SUPPORTED_LNGS (or the fallback).
export const LANGUAGE_OPTIONS = {
  supportedLngs: SUPPORTED_LNGS,
  load: 'languageOnly' as const,
  fallbackLng: 'en',
  detection: DETECTION_OPTIONS,
};
